import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import { Button, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf } from "capitalrx-components-2.0/lib/layout";

import type { Map, MapConstructorOptions, MapKit, MarkerAnnotation, Place, Search } from "./mapkit";
import { AppleMapProps, AppleMapRef, MapBounds, MapLocation, MapQuery } from "./types";
import { calculateRegionForAnnotations, loadMapKitScript, syncRender } from "./utils";

import styles from "./styles.module.scss";

// Default to centering on the US
export const DEFAULT_MAP_COORDINATES = {
  lat: 40.7045389,
  lng: -74.0133233,
};

interface DefaultMapSearchProps {
  isLoading?: boolean;
  label: string;
  onSearchArea?: (bounds: MapBounds | null) => void;
  getCurrentBounds: () => MapBounds | null;
}

const DefaultMapSearch = (props: DefaultMapSearchProps) => {
  return (
    <Button
      testId="redo-map-search-button"
      disabled={props.isLoading}
      size="large"
      onClick={() => {
        props.onSearchArea?.(props.getCurrentBounds());
      }}
    >
      <Shelf spacing="mini" className={styles["RedoSearchOnSpinner"]}>
        {props.isLoading && <Spinner className="ReDoSearchOnMapSpinner" />}
        <span>{props.label}</span>
      </Shelf>
    </Button>
  );
};

const AppleMap = forwardRef<AppleMapRef, AppleMapProps>(
  (
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      token = (site as any).appleMapToken || process.env.CAPITALRX_APPLE_MAP_TOKEN || process.env.REACT_APP_TEMP_APPLE_MAP_TOKEN,
      locations = [],
      className,
      style,
      mapType = "standard",
      showsCompass = "adaptive",
      showsMapTypeControl = false,
      showsZoomControl = true,
      showsUserLocationControl = false,
      showsPointsOfInterest = true,
      showsScale = "adaptive",
      language,
      padding = { top: 50, right: 50, bottom: 50, left: 50 },
      center,
      query,
      onMapLoad,
      onSearchArea,
      renderToolbar,
      renderCalloutContent,
      renderCalloutRightAccessory,
      renderCalloutLeftAccessory,
      isLoading,
    },
    ref,
  ) => {
    const { t: translate, i18n } = useTranslation();

    const [isLoaded, setIsLoaded] = useState(false);
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<Map | null>(null);
    const annotationsRef = useRef<MarkerAnnotation[]>([]);
    const searchRef = useRef<Search | null>(null);
    const mapkitRef = useRef<MapKit | (() => MapKit) | null>(null);

    const createAnnotation = useCallback(
      (location: MapLocation): MarkerAnnotation => {
        const mapkit = mapkitRef.current as MapKit;
        if (!mapkit) {
          throw new Error("MapKit not available");
        }

        const annotation = new mapkit.MarkerAnnotation(new mapkit.Coordinate(location.latitude, location.longitude), {
          title: location.title,
          subtitle: location.subtitle,
          color: location.color,
          clusteringIdentifier: location.clusteringIdentifer ?? "all",
        });

        annotation.callout = {};

        if (renderCalloutContent) {
          annotation.callout.calloutContentForAnnotation = () => syncRender(renderCalloutContent(location));
        }
        if (renderCalloutLeftAccessory) {
          annotation.callout.calloutLeftAccessoryForAnnotation = () => syncRender(renderCalloutLeftAccessory(location));
        }
        if (renderCalloutRightAccessory) {
          annotation.callout.calloutRightAccessoryForAnnotation = () => syncRender(renderCalloutRightAccessory(location));
        }

        return annotation;
      },
      [renderCalloutContent, renderCalloutRightAccessory, renderCalloutLeftAccessory],
    );

    const createAnnotations = useCallback(
      (locations: MapLocation[]): MarkerAnnotation[] => {
        return locations?.map(createAnnotation);
      },
      [createAnnotation],
    );

    const updateAnnotations = useCallback(
      (locations: MapLocation[]) => {
        const map = mapInstanceRef.current;
        const mapkit = mapkitRef.current as MapKit;
        if (!map || !mapkit) return;

        // Remove existing annotations
        if (annotationsRef.current.length > 0) {
          map.removeAnnotations(annotationsRef.current);
        }

        if (isLoading) {
          return;
        }

        // Create and add new annotations
        const newAnnotations = createAnnotations(locations);
        annotationsRef.current = newAnnotations;

        if (newAnnotations.length > 0) {
          map.addAnnotations(newAnnotations);

          // Auto-center map to show all annotations
          const region = calculateRegionForAnnotations(mapkit, newAnnotations);
          if (region) {
            map.region = region;
            const mapkitPadding = new mapkit.Padding(padding.top, padding.right, padding.bottom, padding.left);
            map.showItems(newAnnotations, {
              animate: true,
              padding: mapkitPadding,
            });
          }
        }
      },
      [createAnnotations, padding, isLoading],
    );

    const getCurrentBounds = useCallback((): MapBounds | null => {
      const map = mapInstanceRef.current;
      if (!map) return null;

      const region = map.region;
      const center = region.center;
      const span = region.span;

      const northEast = {
        latitude: center.latitude + span.latitudeDelta / 2,
        longitude: center.longitude + span.longitudeDelta / 2,
      };

      const northWest = {
        latitude: center.latitude + span.latitudeDelta / 2,
        longitude: center.longitude - span.longitudeDelta / 2,
      };

      const southEast = {
        latitude: center.latitude - span.latitudeDelta / 2,
        longitude: center.longitude + span.longitudeDelta / 2,
      };

      const southWest = {
        latitude: center.latitude - span.latitudeDelta / 2,
        longitude: center.longitude - span.longitudeDelta / 2,
      };

      return {
        northEast,
        northWest,
        southEast,
        southWest,
        center: {
          latitude: center.latitude,
          longitude: center.longitude,
        },
      };
    }, []);

    const addQuerySearchAnnotations = useCallback(
      (query?: MapQuery) => {
        if (!query || !searchRef.current) return;

        const search = searchRef.current;

        search.search(query.query, (error, data) => {
          if (error) {
            return;
          }
          if (!data) return;
          const { places } = data;
          if (!places || places.length === 0) return;
          const annotations = places
            .filter((place: Place) => place.coordinate)
            .map((place: Place) => ({
              latitude: place.coordinate.latitude,
              longitude: place.coordinate.longitude,
              title: query.title || place.name || place.formattedAddress || "Service Facility",
              subtitle: query.subtitle || undefined,
              color: query.color || undefined,
            }));
          if (annotations.length > 0) {
            updateAnnotations(annotations);
          }
        });
      },
      [updateAnnotations],
    );

    // Initialize map when token changes
    useEffect(() => {
      const initializeMap = async () => {
        if (!token || !mapRef.current) {
          return;
        }

        // Clean up existing map
        if (mapInstanceRef.current) {
          mapInstanceRef.current.destroy();
          mapInstanceRef.current = null;
        }

        try {
          // Load MapKit JS and get the instance
          const mapkit: MapKit = await loadMapKitScript();
          mapkitRef.current = mapkit;

          // Initialize MapKit
          mapkit.init({
            authorizationCallback: (done: (t: string) => void) => done(token),
            language: language ?? i18n.language,
          });

          const mapProps: MapConstructorOptions = {
            mapType,
            showsCompass,
            showsMapTypeControl,
            showsZoomControl,
            showsUserLocationControl,
            showsPointsOfInterest,
            showsScale,
          };

          if (center && center.latitude && center.longitude) {
            mapProps.center = new mapkit.Coordinate(center.latitude, center.longitude);
          } else {
            // Center on the US by default
            mapProps.center = new mapkit.Coordinate(DEFAULT_MAP_COORDINATES.lat, DEFAULT_MAP_COORDINATES.lng);
          }

          // Create map instance
          const map = new mapkit.Map(mapRef.current, mapProps);

          mapInstanceRef.current = map;
          mapkit.language = language ?? i18n.language;

          searchRef.current = new mapkit.Search();
          setIsLoaded(true);
          onMapLoad?.(map);
        } catch (error) {
          console.error("Failed to initialize Apple Maps:", error);
        }
      };

      initializeMap();

      return () => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.destroy();
          mapInstanceRef.current = null;
        }
        mapkitRef.current = null;
      };
    }, [
      token,
      mapType,
      showsCompass,
      showsMapTypeControl,
      showsZoomControl,
      showsUserLocationControl,
      showsPointsOfInterest,
      showsScale,
      language,
      i18n.language,
    ]);

    useEffect(() => {
      if (isLoaded && locations?.length) {
        updateAnnotations(locations);
      } else if (isLoaded && query) {
        addQuerySearchAnnotations(query);
      }
    }, [isLoaded, locations, center, query, updateAnnotations, addQuerySearchAnnotations]);

    useImperativeHandle(ref, () => ({
      getCurrentBounds,
    }));

    const tools = renderToolbar
      ? renderToolbar({
          getCurrentBounds,
        })
      : [];
    if (!tools.length && onSearchArea) {
      tools.push(
        <DefaultMapSearch
          isLoading={isLoading}
          label={translate("APPLE_MAP.reDoSearchOnMap")}
          onSearchArea={onSearchArea}
          getCurrentBounds={getCurrentBounds}
        />,
      );
    }

    return (
      <div style={style} ref={mapRef} className={`${styles["MapWrapper"]} ${className}`} role="presentation">
        {isLoading && <Skeleton className={styles["MapOverlaySkeleton"]} />}
        <div className={styles["MapOverlayButtons"]}>
          {tools?.map((tool, idx) => (
            <div key={idx}>{tool}</div>
          ))}
        </div>
      </div>
    );
  },
);

export default AppleMap;
